// services/TimelineDelayService.js
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  getDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import NotificationDispatcher from './NotificationDispatcher';

/**
 * Service de gestion automatique des retards de dossiers
 */
class TimelineDelayService {
  
  constructor() {
    // Configuration des étapes clés et leurs délais en jours
    this.keySteps = [
      {
        name: "Réalisation de la déclaration préalable",
        delayInDays: 7,
        category: "administrative"
      },
      {
        name: "Pouvoir Enedis reçu", 
        delayInDays: 30,
        category: "administrative"
      },
      {
        name: "Commande du matériel avec délai de réception",
        delayInDays: 45,
        category: "logistique"
      },
      {
        name: "Accusé réception des livraisons",
        delayInDays: 60,
        category: "logistique"
      },
      {
        name: "Réalisation du PV de réception",
        delayInDays: 90,
        category: "finalisation"
      },
      {
        name: "Demande d'avis client",
        delayInDays: 100,
        category: "satisfaction"
      }
    ];
  }

  /**
   * Vérifier tous les dossiers pour les retards
   */
  async checkAllDossierDelays() {
    try {
      // Récupérer tous les dossiers actifs
      const dossiersQuery = query(
        collection(db, 'dossiers'),
        where('status', 'not-in', ['closed', 'cancelled', 'suspended'])
      );
      
      const dossiersSnapshot = await getDocs(dossiersQuery);
      let delaysDetected = 0;
      
      for (const dossierDoc of dossiersSnapshot.docs) {
        const dossier = { id: dossierDoc.id, ...dossierDoc.data() };
        
        const hasDelays = await this.checkDossierStepDelays(dossier);
        
        if (hasDelays) {
          delaysDetected++;
        }
      }
      
      return {
        totalChecked: dossiersSnapshot.docs.length,
        delaysDetected: delaysDetected
      };
      
    } catch (error) {
      console.error('Erreur lors de la vérification des retards:', error);
      return { totalChecked: 0, delaysDetected: 0 };
    }
  }

  /**
   * Vérifier les retards d'un dossier spécifique
   */
  async checkDossierStepDelays(dossier) {
    try {
      // Récupérer la timeline du dossier
      const timeline = await this.getDossierTimeline(dossier.id);
      
      if (!timeline || !timeline.phases || timeline.phases.length === 0) {
        return false;
      }

      const createdAt = dossier.createdAt?.toDate();
      if (!createdAt) {
        return false;
      }
      
      let hasDelays = false;
      const delayedSteps = [];

      // Vérifier chaque étape clé
      for (const keyStep of this.keySteps) {
        const phase = this.findPhaseByName(timeline.phases, keyStep.name);
        
        if (!phase) {
          continue; // Étape non trouvée, on passe à la suivante
        }
        
        // Calculer la date limite pour cette étape
        const deadline = this.calculateStepDeadline(createdAt, keyStep.delayInDays);
        
        // Vérifier si elle est en retard
        const now = new Date();
        const isCompleted = phase.status?.toLowerCase() === 'terminé';
        const isOverdue = now > deadline && !isCompleted;

        if (isOverdue) {
          const delayDays = Math.floor((now - deadline) / (1000 * 60 * 60 * 24));
          
          delayedSteps.push({
            stepName: keyStep.name,
            deadline: deadline,
            delayDays: delayDays,
            category: keyStep.category
          });
          
          hasDelays = true;
        }
      }

      // Gérer les retards détectés
      if (hasDelays) {
        await this.handleDossierDelays(dossier, delayedSteps);
      } else {
        // ✅ NOUVEAU : Marquer automatiquement comme "en cours" si pas en retard
        await this.markDossierAsInProgress(dossier);
        // Vérifier si le dossier était marqué en retard et peut être récupéré
        await this.checkDossierRecovery(dossier, timeline.phases);
      }

      return hasDelays;

    } catch (error) {
      console.error(`Erreur vérification dossier ${dossier.id}:`, error);
      return false;
    }
  }

  /**
   * Calculer la date limite pour une étape
   */
  calculateStepDeadline(createdAt, delayInDays) {
    const deadline = new Date(createdAt);
    deadline.setDate(deadline.getDate() + delayInDays);
    return deadline;
  }

  /**
   * Récupérer la timeline d'un dossier
   */
  async getDossierTimeline(dossierId) {
    try {
      // Récupérer le dossier directement
      const dossierRef = doc(db, 'dossiers', dossierId);
      const dossierSnap = await getDoc(dossierRef);

      if (!dossierSnap.exists()) {
        return null;
      }

      const dossierData = dossierSnap.data();
      
      // Les phases sont directement dans le dossier
      let allPhases = [
        ...(dossierData.phasesChargeAffaire || []),
        ...(dossierData.phasesChefProjet || [])
      ];

      if (allPhases.length === 0) {
        // Utiliser les phases par défaut comme dans DualTimeline.js
        const defaultChargeAffairePhases = [
          { name: "Dossier signé", date: "", status: "Non réalisé" },
          { name: "Facture d'acompte N°1 transmise au client", date: "", status: "Non réalisé" },
          { name: "Acompte N°1 payé", date: "", status: "Non réalisé" },
          { name: "Réalisation de la déclaration préalable", date: "", status: "Non réalisé" },
          { name: "Transmission du pouvoir Enedis au client", date: "", status: "Non réalisé" },
          { name: "Autorisation DP reçue", date: "", status: "Non réalisé" },
          { name: "Pouvoir Enedis reçu", date: "", status: "Non réalisé" }
        ];

        const defaultChefProjetPhases = [
          { name: "Réception des informations du chargé d'affaire", date: "", status: "Non réalisé" },
          { name: "Organisation de la visite technique", date: "", status: "Non réalisé" },
          { name: "Relevés des passages de câbles", date: "", status: "Non réalisé" },
          { name: "Réalisation des plans de chaînes", date: "", status: "Non réalisé" },
          { name: "Réalisation des calculs et calepinages des structures", date: "", status: "Non réalisé" },
          { name: "Lister le matériel nécessaire", date: "", status: "Non réalisé" },
          { name: "Demande de raccordement Enedis", date: "", status: "Non réalisé" },
          { name: "Demande de devis à au moins 2 fournisseurs", date: "", status: "Non réalisé" },
          { name: "Réception et analyse des offres", date: "", status: "Non réalisé" },
          { name: "Transmission de la facture de situation N°2", date: "", status: "Non réalisé" },
          { name: "Commande du matériel avec délai de réception", date: "", status: "Non réalisé" },
          { name: "Communication du planning au client", date: "", status: "Non réalisé" },
          { name: "Organisation des moyens humains pour le chantier", date: "", status: "Non réalisé" },
          { name: "Création de la fiche de chantier", date: "", status: "Non réalisé" },
          { name: "Accusé réception des livraisons", date: "", status: "Non réalisé" },
          { name: "Transmission de la facture de situation N°3", date: "", status: "Non réalisé" },
          { name: "Date de début des travaux", date: "", status: "Non réalisé" },
          { name: "Organisation des visites de suivi et d'inspection", date: "", status: "Non réalisé" },
          { name: "Organisation de la mise en service de l'installation", date: "", status: "Non réalisé" },
          { name: "Réalisation du consuel", date: "", status: "Non réalisé" },
          { name: "Réalisation du PV de réception", date: "", status: "Non réalisé" },
          { name: "Transmission de la facture de solde", date: "", status: "Non réalisé" },
          { name: "Demande d'avis client", date: "", status: "Non réalisé" }
        ];
        
        allPhases = [...defaultChargeAffairePhases, ...defaultChefProjetPhases];
      }

      if (allPhases.length === 0) {
        return null;
      }

      return {
        id: dossierId,
        dossierId: dossierId,
        phases: allPhases
      };
      
    } catch (error) {
      console.error(`Erreur récupération timeline ${dossierId}:`, error);
      return null;
    }
  }

  /**
   * Rechercher une phase par son nom
   */
  findPhaseByName(phases, stepName) {
    return phases.find(phase => 
      phase.name?.toLowerCase().trim() === stepName.toLowerCase().trim()
    );
  }

  /**
   * Gérer les retards détectés pour un dossier
   */
  async handleDossierDelays(dossier, delayedSteps) {
    try {
      console.log(`🚨 DELAY-SERVICE: Gestion des retards pour le dossier ${dossier.id}`);
      
      // Marquer le dossier comme en retard
      await this.markDossierAsOverdue(dossier.id, delayedSteps);
      
      // Notifier les administrateurs si ce n'est pas déjà fait
      const shouldNotify = !dossier.delayNotificationSent;
      
      if (shouldNotify) {
        await this.notifyAdministrators(dossier, delayedSteps);
      }
      
    } catch (error) {
      console.error(`❌ DELAY-SERVICE: Erreur gestion retards ${dossier.id}:`, error);
    }
  }

  /**
   * Marquer un dossier comme en cours (si pas déjà défini)
   */
  async markDossierAsInProgress(dossier) {
    try {
      // Ne marquer que si le dossier n'a pas encore de statut défini ou est "à démarrer"
      const currentStatus = dossier.status?.toLowerCase() || dossier.statut?.toLowerCase();
      
      if (!currentStatus || currentStatus === 'à démarrer' || currentStatus === 'undefined' || currentStatus === 'null') {
        const dossierRef = doc(db, 'dossiers', dossier.id);
        
        const updateData = {
          status: 'en cours',
          statut: 'en cours',
          startedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await updateDoc(dossierRef, updateData);
      }
      
    } catch (error) {
      console.error(`Erreur marquage en cours ${dossier.id}:`, error);
    }
  }

  /**
   * Marquer un dossier comme en retard
   */
  async markDossierAsOverdue(dossierId, delayedSteps) {
    try {
      const dossierRef = doc(db, 'dossiers', dossierId);
      
      const updateData = {
        status: 'en retard',
        statut: 'en retard',
        delayDetectedAt: serverTimestamp(),
        delayedSteps: delayedSteps,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(dossierRef, updateData);
      
    } catch (error) {
      console.error(`Erreur marquage retard ${dossierId}:`, error);
    }
  }

  /**
   * Notifier les administrateurs d'un retard
   */
  async notifyAdministrators(dossier, delayedSteps) {
    try {
      console.log(`📧 DELAY-SERVICE: Notification retard pour ${dossier.id}`);
      
      // Récupérer les administrateurs du dossier
      const administrators = await this.getDossierAdministrators(dossier.id);
      
      if (administrators.length === 0) {
        console.log(`⚠️ DELAY-SERVICE: Aucun administrateur trouvé pour ${dossier.id}`);
        return;
      }

      // Créer la notification
      const notificationData = {
        type: 'dossier_delay',
        title: `Retard détecté - ${dossier.clientName}`,
        message: `Le dossier ${dossier.clientName} a ${delayedSteps.length} étape(s) en retard.`,
        dossierId: dossier.id,
        dossierName: dossier.clientName,
        delayedSteps: delayedSteps,
        priority: 'high',
        recipientIds: administrators.map(admin => admin.id),
        metadata: {
          totalDelayedSteps: delayedSteps.length,
          maxDelayDays: Math.max(...delayedSteps.map(step => step.delayDays))
        }
      };

      // Envoyer la notification via le dispatcher
      await NotificationDispatcher.createNotification(notificationData);
      
      // Marquer que la notification a été envoyée
      const dossierRef = doc(db, 'dossiers', dossier.id);
      await updateDoc(dossierRef, {
        delayNotificationSent: true,
        delayNotificationSentAt: serverTimestamp()
      });
      
      console.log(`✅ DELAY-SERVICE: Notification envoyée pour ${dossier.id}`);
      
    } catch (error) {
      console.error(`❌ DELAY-SERVICE: Erreur notification ${dossier.id}:`, error);
    }
  }

  /**
   * Récupérer les administrateurs d'un dossier
   */
  async getDossierAdministrators(dossierId) {
    try {
      // Récupérer tous les utilisateurs administrateurs
      const usersQuery = query(
        collection(db, 'users'),
        where('role', 'in', ['admin', 'super_admin'])
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      
      return usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
    } catch (error) {
      console.error(`Erreur récupération admins:`, error);
      return [];
    }
  }

  /**
   * Vérifier si un dossier peut être récupéré (plus en retard)
   */
  async checkDossierRecovery(dossier, phases) {
    try {
      // Si le dossier n'était pas marqué en retard, rien à faire
      if (dossier.status !== 'en retard' && dossier.statut !== 'en retard') {
        return;
      }
      
      const createdAt = dossier.createdAt?.toDate();
      if (!createdAt) {
        return;
      }

      // Vérifier si toutes les étapes en retard sont maintenant terminées
      let hasActiveDelays = false;
      
      for (const keyStep of this.keySteps) {
        const phase = this.findPhaseByName(phases, keyStep.name);
        
        if (!phase) {
          continue;
        }

        const deadline = this.calculateStepDeadline(createdAt, keyStep.delayInDays);
        const now = new Date();
        const isCompleted = phase.status?.toLowerCase() === 'terminé';
        const isOverdue = now > deadline && !isCompleted;

        if (isOverdue) {
          hasActiveDelays = true;
          break;
        }
      }

      if (!hasActiveDelays) {
        await this.restoreDossierFromDelay(dossier.id);
      }

    } catch (error) {
      console.error(`Erreur vérification récupération ${dossier.id}:`, error);
    }
  }

  /**
   * Restaurer un dossier qui n'est plus en retard
   */
  async restoreDossierFromDelay(dossierId) {
    try {
      const dossierRef = doc(db, 'dossiers', dossierId);
      
      const updateData = {
        status: 'en cours',
        statut: 'en cours',
        delayRecoveredAt: serverTimestamp(),
        delayNotificationSent: false,
        updatedAt: serverTimestamp()
      };
      
      // Garder l'historique des retards pour référence
      // On ne supprime pas delayDetectedAt et delayedSteps
      
      await updateDoc(dossierRef, updateData);
      
    } catch (error) {
      console.error(`Erreur restauration ${dossierId}:`, error);
    }
  }
}

export default new TimelineDelayService();